import React from 'react';
import { motion } from 'framer-motion';
import { LifeBuoy, BookOpen, MessageCircle, Mail, Phone, Clock, Users, Zap } from 'lucide-react';
import { useReducedMotion } from '../hooks/useReducedMotion';

interface SupportProps {
  onNavigateToFAQ?: () => void;
  onNavigateToContact?: () => void;
  onNavigateToBlog?: () => void;
}

const supportOptions = [
  {
    icon: BookOpen,
    title: 'FAQ & Help Center',
    description: 'Find quick answers to common questions about Octify features and functionality.',
    action: 'faq',
    color: 'from-blue-500 to-cyan-500',
    features: ['Instant answers', 'Searchable content', 'Category filters']
  },
  {
    icon: MessageCircle,
    title: 'Contact Support',
    description: 'Get personalized help from our support team via email or live chat.',
    action: 'contact',
    color: 'from-orange-500 to-pink-500',
    features: ['24-hour response', 'Expert guidance', 'Priority support']
  },
  {
    icon: Users,
    title: 'Community Forum',
    description: 'Connect with other Octify users to share experiences and tips.',
    action: 'community',
    color: 'from-purple-500 to-indigo-500',
    features: ['Peer support', 'Success stories', 'Expert Q&A sessions']
  },
  {
    icon: BookOpen,
    title: 'Knowledge Base',
    description: 'Comprehensive guides, tutorials, and best practices for relationship growth.',
    action: 'blog',
    color: 'from-green-500 to-teal-500',
    features: ['Step-by-step guides', 'Video tutorials', 'Expert articles']
  }
];

const quickLinks = [
  { title: 'Getting Started Guide', category: 'Setup' },
  { title: 'AI Insights Explained', category: 'Features' },
  { title: 'Wearable Integration', category: 'Technical' },
  { title: 'Privacy & Security', category: 'Account' },
  { title: 'Billing & Subscriptions', category: 'Billing' },
  { title: 'VR Setup Guide', category: 'Features' }
];

export const Support: React.FC<SupportProps> = ({ 
  onNavigateToFAQ, 
  onNavigateToContact, 
  onNavigateToBlog 
}) => {
  const prefersReducedMotion = useReducedMotion();

  const handleSupportAction = (action: string) => {
    switch (action) {
      case 'faq':
        onNavigateToFAQ?.();
        break;
      case 'contact':
        onNavigateToContact?.();
        break;
      case 'blog':
        onNavigateToBlog?.();
        break;
      case 'community':
        console.log('Navigate to community forum');
        break;
      default:
        console.log(`Support action: ${action}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-purple-50 to-teal-100 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-teal-900/20 pt-20">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute inset-0 opacity-30"
          animate={prefersReducedMotion ? {} : {
            background: [
              "radial-gradient(circle at 20% 80%, #3B82F6 0%, transparent 50%)",
              "radial-gradient(circle at 80% 20%, #8B5CF6 0%, transparent 50%)",
              "radial-gradient(circle at 40% 40%, #06B6D4 0%, transparent 50%)",
            ]
          }}
          transition={{ duration: 8, repeat: Infinity }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-6">
            <LifeBuoy className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            How can we help you?
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Get the support you need to make the most of your relationship journey with Octify.
          </p>
        </motion.div>

        {/* Support Options Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {supportOptions.map((option, index) => (
            <motion.div
              key={option.title}
              className="group relative"
              initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              whileHover={prefersReducedMotion ? {} : { y: -5 }}
            >
              <div className="h-full p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 hover:border-white/40 dark:hover:border-gray-600/40 transition-all duration-300">
                {/* Icon */}
                <motion.div
                  className={`inline-flex p-4 rounded-2xl bg-gradient-to-r ${option.color} mb-6`}
                  whileHover={prefersReducedMotion ? {} : { scale: 1.1, rotate: 5 }}
                  transition={{ duration: 0.3 }}
                >
                  <option.icon className="w-8 h-8 text-white" />
                </motion.div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {option.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  {option.description}
                </p>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {option.features.map((feature, featureIndex) => (
                    <li key={feature} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                      <div className="w-1.5 h-1.5 bg-gradient-to-r from-orange-500 to-pink-500 rounded-full" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Action Button */}
                <motion.button
                  onClick={() => handleSupportAction(option.action)}
                  className={`w-full py-3 rounded-full font-medium transition-all duration-300 bg-gradient-to-r ${option.color} text-white hover:shadow-lg`}
                  whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
                  whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
                >
                  Get Help
                </motion.button>

                {/* Glow Effect */}
                <div className={`absolute inset-0 rounded-3xl bg-gradient-to-r ${option.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 pointer-events-none`} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Quick Links */}
        <motion.div
          className="mb-16"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Popular Help Topics
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {quickLinks.map((link, index) => (
              <motion.button
                key={link.title}
                onClick={() => onNavigateToBlog?.()}
                className="p-4 rounded-2xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-sm border border-white/20 dark:border-gray-700/20 hover:bg-white/20 dark:hover:bg-gray-800/20 transition-all duration-200 text-left group"
                initial={prefersReducedMotion ? {} : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.05 }}
                whileHover={prefersReducedMotion ? {} : { scale: 1.02 }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900 dark:text-white group-hover:text-orange-500 transition-colors duration-200">
                      {link.title}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {link.category}
                    </p>
                  </div>
                  <motion.div
                    className="text-gray-400 group-hover:text-orange-500 transition-colors duration-200"
                    whileHover={prefersReducedMotion ? {} : { x: 5 }}
                  >
                    →
                  </motion.div>
                </div>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          className="grid md:grid-cols-3 gap-8"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          {/* Response Times */}
          <div className="p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-full mb-4">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Quick Response
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              We typically respond within 24 hours during business days.
            </p>
          </div>

          {/* Emergency Support */}
          <div className="p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-red-500 to-orange-500 rounded-full mb-4">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Priority Support
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Premium users get priority support with faster response times.
            </p>
          </div>

          {/* Community */}
          <div className="p-8 rounded-3xl bg-white/10 dark:bg-gray-800/10 backdrop-blur-xl border border-white/20 dark:border-gray-700/20 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full mb-4">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Community Help
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              Connect with other users and get peer support 24/7.
            </p>
          </div>
        </motion.div>

        {/* Emergency Contact */}
        <motion.div
          className="mt-16 p-8 rounded-3xl bg-gradient-to-r from-orange-500/10 to-pink-500/10 border border-orange-200/30 dark:border-orange-700/30 text-center"
          initial={prefersReducedMotion ? {} : { opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Need Immediate Help?
          </h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            For urgent technical issues or account problems, reach out directly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              onClick={onNavigateToContact}
              className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-full font-medium flex items-center gap-2 justify-center"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            >
              <Mail className="w-5 h-5" />
              urgent@octify.app
            </motion.button>
            <motion.button
              className="px-6 py-3 bg-white/10 dark:bg-gray-800/10 text-gray-900 dark:text-white rounded-full font-medium border border-white/20 dark:border-gray-700/20 hover:bg-white/20 dark:hover:bg-gray-800/20 transition-colors duration-200 flex items-center gap-2 justify-center"
              whileHover={prefersReducedMotion ? {} : { scale: 1.05 }}
              whileTap={prefersReducedMotion ? {} : { scale: 0.95 }}
            >
              <Phone className="w-5 h-5" />
              Live Chat
            </motion.button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};